import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Plus, Trash2, Trophy, CreditCard, Layers, Info } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import ImageUpload from "@/components/ImageUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DISCIPLINES, SKILLS, GENDERS, FORMATS, PLAYOFF_FORMATS, CURRENCIES, categoryLabel, fmtMoney } from "@/lib/tournament";
import CategoryDraftFields, { PLAYOFF_SCORING_PRESETS, scoringLabel } from "@/components/tournament/CategoryDraftFields";
import api, { formatApiError } from "@/lib/api";

const STEPS = [
  { id: 1, label: "Event Details", Icon: Trophy },
  { id: 2, label: "Payment", Icon: CreditCard },
  { id: 3, label: "Categories", Icon: Layers },
  { id: 4, label: "Review", Icon: Check },
];

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "w-full rounded-xl bg-surface glow-border px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-400";

function Dropdown({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
        {options.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`${testid}-${o.v}`}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function TournamentWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    name: "", venue: "", start_date: "", end_date: "", organizer_name: "", organizer_contact: "",
    banner_url: null, currency: "PHP", entry_fee: "", payment_qr_url: null, payment_details: "",
    categories: [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [draft, setDraft] = useState({ discipline: "doubles", skill_level: "open", gender: "mixed", format: "single_elim", playoff_format: "single_elim", pools: 1, scoring: PLAYOFF_SCORING_PRESETS[0].s });

  const addCategory = () => {
    const c = { id: `cat_${Date.now()}`, ...draft };
    const dup = form.categories.some((x) => x.discipline === c.discipline && x.skill_level === c.skill_level && x.gender === c.gender && x.format === c.format);
    if (dup) { toast.error("That category combination already exists"); return; }
    set("categories", [...form.categories, c]);
  };
  const removeCategory = (id) => set("categories", form.categories.filter((c) => c.id !== id));

  const canNext = () => {
    if (step === 1) return form.name.trim().length > 0;
    return true;
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Tournament name is required"); setStep(1); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        entry_fee: Number(form.entry_fee) || 0,
        categories: form.categories.map(({ id, discipline, skill_level, gender, format, playoff_format, pools, scoring }) => ({ id, discipline, skill_level, gender, format, playoff_format: playoff_format || "single_elim", pools: pools || 1, scoring: scoring || undefined })),
      };
      const { data } = await api.post("/tournaments", payload);
      toast.success("Tournament created");
      nav(`/tournaments/${data.tournament_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create tournament");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="New Tournament" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 grain">
        <button data-testid="wizard-back-list" onClick={() => nav("/tournaments")} className="text-sm flex items-center gap-2 mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft className="w-4 h-4" /> All tournaments
        </button>

        {/* stepper */}
        <div className="flex items-center gap-2 mb-8" data-testid="wizard-stepper">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors"
                  style={step >= s.id ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--surface)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                  {step > s.id ? <Check className="w-4 h-4" /> : <s.Icon className="w-4 h-4" />}
                </div>
                <span className="hidden sm:inline text-xs font-bold" style={{ color: step >= s.id ? "var(--text)" : "var(--text-2)" }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px" style={{ background: step > s.id ? "var(--primary)" : "var(--border)" }} />}
            </div>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
          className="rounded-2xl glow-border p-6 space-y-5" style={{ background: "var(--surface)" }} data-testid={`wizard-step-${step}`}>

          {step === 1 && (
            <>
              <h2 className="font-display font-bold text-xl">Event details</h2>
              <Field label="Tournament name" testid="field-name">
                <input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Metro Open 2026" className={inputCls} style={{ color: "var(--text)" }} />
              </Field>
              <Field label="Venue" testid="field-venue">
                <input data-testid="input-venue" value={form.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Court complex / arena" className={inputCls} style={{ color: "var(--text)" }} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Start date" testid="field-start"><input data-testid="input-start" type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={inputCls} style={{ color: "var(--text)" }} /></Field>
                <Field label="End date" testid="field-end"><input data-testid="input-end" type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className={inputCls} style={{ color: "var(--text)" }} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Organizer name" testid="field-org-name"><input data-testid="input-org-name" value={form.organizer_name} onChange={(e) => set("organizer_name", e.target.value)} placeholder="Your name / club" className={inputCls} style={{ color: "var(--text)" }} /></Field>
                <Field label="Organizer contact" testid="field-org-contact"><input data-testid="input-org-contact" value={form.organizer_contact} onChange={(e) => set("organizer_contact", e.target.value)} placeholder="Email or phone" className={inputCls} style={{ color: "var(--text)" }} /></Field>
              </div>
              <ImageUpload testid="banner-upload" label="Banner image" value={form.banner_url} onChange={(v) => set("banner_url", v)} hint="3:1 wide · under 8MB" />
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-display font-bold text-xl">Payment & fees</h2>
              <p className="text-xs flex items-start gap-2" style={{ color: "var(--text-2)" }}><Info className="w-4 h-4 shrink-0 text-primary-c" /> Players pay manually and upload a payment screenshot. You confirm each registration in the roster.</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Currency" testid="field-currency"><Dropdown testid="input-currency" value={form.currency} onChange={(v) => set("currency", v)} options={CURRENCIES} /></Field>
                <Field label="Entry fee (per player)" testid="field-fee"><input data-testid="input-fee" type="number" min="0" value={form.entry_fee} onChange={(e) => set("entry_fee", e.target.value)} placeholder="0" className={inputCls} style={{ color: "var(--text)" }} /></Field>
              </div>
              <ImageUpload testid="qr-upload" label="Payment QR code" value={form.payment_qr_url} onChange={(v) => set("payment_qr_url", v)} aspect="aspect-square" hint="GCash / bank QR" />
              <Field label="Payment details / instructions" testid="field-pay-details">
                <textarea data-testid="input-pay-details" rows={4} value={form.payment_details} onChange={(e) => set("payment_details", e.target.value)}
                  placeholder="e.g. GCash 0917-123-4567 (Juan D.) — send screenshot after paying." className={`${inputCls} resize-none`} style={{ color: "var(--text)" }} />
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-display font-bold text-xl">Category matrix</h2>
              <p className="text-xs" style={{ color: "var(--text-2)" }}>Combine discipline, skill tier, gender division and bracket format. Set pools to split a category into separate flights (Pool A, Pool B…).</p>
              <div className="rounded-xl p-4" style={{ background: "var(--bg)" }} data-testid="category-builder">
                <CategoryDraftFields draft={draft} setDraft={setDraft} idPrefix="draft" />
              </div>
              <button data-testid="add-category" onClick={addCategory} className="btn-glow rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add category</button>

              <div className="space-y-2" data-testid="category-list">
                {form.categories.length === 0 ? (
                  <p className="text-sm text-center py-6" style={{ color: "var(--text-2)" }}>No categories added yet.</p>
                ) : form.categories.map((c) => (
                  <div key={c.id} data-testid={`category-item-${c.id}`} className="flex items-center justify-between rounded-xl px-4 py-3 glass">
                    <div>
                      <div className="font-bold text-sm">{categoryLabel(c)}</div>
                      <div className="text-[10px] mono uppercase tracking-wider mt-0.5" style={{ color: "var(--text-2)" }}>{FORMATS.find((f) => f.v === c.format)?.l}{(c.pools || 1) > 1 ? ` · ${c.pools} pools · playoff: ${PLAYOFF_FORMATS.find((f) => f.v === (c.playoff_format || "single_elim"))?.l}` : ""}{scoringLabel(c.scoring) ? ` · ${scoringLabel(c.scoring)}` : ""}</div>
                    </div>
                    <button data-testid={`remove-category-${c.id}`} onClick={() => removeCategory(c.id)} className="w-8 h-8 rounded-full glass flex items-center justify-center"><Trash2 className="w-4 h-4" style={{ color: "#ef4444" }} /></button>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="font-display font-bold text-xl">Review & create</h2>
              <div className="rounded-xl p-4 space-y-3" style={{ background: "var(--bg)" }} data-testid="review-summary">
                <Row k="Name" v={form.name || "—"} />
                <Row k="Venue" v={form.venue || "—"} />
                <Row k="Dates" v={[form.start_date, form.end_date].filter(Boolean).join(" → ") || "—"} />
                <Row k="Entry fee" v={fmtMoney(form.currency, form.entry_fee || 0)} />
                <Row k="Categories" v={`${form.categories.length}`} />
              </div>
              {form.categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {form.categories.map((c) => <span key={c.id} className="text-xs glass rounded-full px-3 py-1" style={{ color: "var(--text-2)" }}>{categoryLabel(c)}</span>)}
                </div>
              )}
              {form.categories.length === 0 && <p className="text-xs" style={{ color: "var(--text-2)" }}>Tip: you can add categories later from the tournament page too.</p>}
            </>
          )}
        </motion.div>

        {/* nav buttons */}
        <div className="flex items-center justify-between mt-6">
          <button data-testid="wizard-prev" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
            className="glass rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-40" style={{ color: "var(--text)" }}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 4 ? (
            <button data-testid="wizard-next" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Tournament name is required")}
              className="btn-glow rounded-full px-6 py-2.5 text-sm font-bold flex items-center gap-2">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="wizard-create" onClick={submit} disabled={busy}
              className="btn-glow rounded-full px-6 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Create tournament</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span style={{ color: "var(--text-2)" }}>{k}</span>
      <span className="font-bold text-right">{v}</span>
    </div>
  );
}
